import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, AlertTriangle, Shield, Info, FileText } from "lucide-react";

export default function Disclosures() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
          <div className="flex gap-2">
            <Link href="/privacy">
              <Button variant="outline" size="sm">
                Privacy Policy
              </Button>
            </Link>
            <Link href="/terms">
              <Button variant="outline" size="sm">
                Terms of Use
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="Tradeharbor.io" className="h-20 w-auto" />
          </div>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <FileText className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Important Disclosures</h1>
          <p className="text-lg text-muted-foreground">
            Last Updated: February 11, 2026
          </p>
        </div>

        {/* Main Content */}
        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          
          <Card className="border-red-500/50 bg-red-500/10 my-8">
            <CardContent className="py-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-lg mb-2 m-0">Risk Disclosure</h3>
                  <p className="text-sm m-0">
                    Trading securities involves substantial risk of loss and is not suitable for all investors. 
                    You may lose some or all of your invested capital. Past performance is not indicative of 
                    future results. Please carefully consider your financial situation and investment objectives 
                    before trading.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <section>
            <h2>Brokerage Services</h2>
            
            <Card className="my-4">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <p className="font-semibold mb-2">Securities Offered Through Alpaca Markets, Inc.</p>
                    <ul className="text-sm space-y-1 m-0">
                      <li>Member FINRA (www.finra.org)</li>
                      <li>Member SIPC (www.sipc.org)</li>
                      <li>Alpaca Markets, Inc. is a registered broker-dealer</li>
                      <li>SIPC protection covers up to $500,000 (including $250,000 for cash claims)</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3>Tradeharbor.io is NOT a Broker-Dealer</h3>
            <p>
              Tradeharbor.io is a technology platform that provides software and tools to facilitate 
              trading through third-party broker-dealers. We do not provide brokerage services, execute 
              trades directly, or hold customer securities or funds.
            </p>
          </section>

          <section>
            <h2>Investment Risks</h2>
            
            <h3>General Market Risk</h3>
            <p>
              Securities markets are volatile and subject to fluctuation. Market conditions can change 
              rapidly, and the value of your investments can go up or down. There is no guarantee that 
              any investment strategy will be successful.
            </p>

            <h3>Specific Risks Include:</h3>
            <ul>
              <li><strong>Market Volatility:</strong> Rapid price movements can result in significant losses</li>
              <li><strong>Liquidity Risk:</strong> Inability to buy or sell securities at desired prices</li>
              <li><strong>Leverage/Margin Risk:</strong> Borrowing to trade amplifies both gains and losses</li>
              <li><strong>Options Risk:</strong> Options can expire worthless, resulting in 100% loss of premium paid</li>
              <li><strong>Concentration Risk:</strong> Holding a concentrated position increases exposure to specific risks</li>
              <li><strong>Technology Risk:</strong> System failures, outages, or errors may prevent order execution</li>
            </ul>

            <h3>Automated Trading Risks</h3>
            <p>
              Automated trading systems and AI-powered features carry additional risks:
            </p>
            <ul>
              <li>Algorithms may execute unintended trades due to programming errors</li>
              <li>System outages may prevent monitoring or stopping automated strategies</li>
              <li>Market conditions may change faster than algorithms can adapt</li>
              <li>AI sentiment analysis may be inaccurate or based on incomplete information</li>
              <li>Past performance of automated strategies does not guarantee future results</li>
            </ul>
          </section>

          <section>
            <h2>Not Investment Advice</h2>
            
            <Card className="border-yellow-500/50 bg-yellow-500/10 my-4">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-yellow-600 mt-1" />
                  <div className="text-sm">
                    <p className="font-semibold mb-2">Educational and Informational Purposes Only</p>
                    <p className="m-0">
                      All information, analysis, and tools provided by Tradeharbor.io are for educational 
                      and informational purposes only. Nothing on this platform constitutes personalized 
                      investment advice or a recommendation to buy, sell, or hold any security.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <p>
              Tradeharbor.io does not provide investment advice, tax advice, or legal advice. You should 
              consult with qualified professionals regarding your specific situation before making any 
              investment decisions.
            </p>
          </section>

          <section>
            <h2>AI-Powered Features</h2>
            
            <h3>Sentiment Analysis Disclaimer</h3>
            <p>
              Our AI-powered sentiment analysis feature uses artificial intelligence (Claude by Anthropic) 
              to analyze news articles and market information. This analysis:
            </p>
            <ul>
              <li>Is provided for informational purposes only</li>
              <li>Does NOT constitute investment advice or recommendations</li>
              <li>May contain errors, inaccuracies, or incomplete information</li>
              <li>Should NOT be the sole basis for investment decisions</li>
              <li>Is based on publicly available information that may be outdated</li>
            </ul>

            <h3>AI Limitations</h3>
            <p>
              AI systems have inherent limitations:
            </p>
            <ul>
              <li>Cannot predict future market movements with certainty</li>
              <li>May misinterpret context or nuance in news articles</li>
              <li>Are trained on historical data that may not reflect current conditions</li>
              <li>Can produce different results for similar queries</li>
              <li>Should be used as one input among many in your decision-making process</li>
            </ul>
          </section>

          <section>
            <h2>Paper Trading vs. Live Trading</h2>
            
            <h3>Paper Trading Limitations</h3>
            <p>
              Paper trading (virtual trading with simulated money) has important limitations:
            </p>
            <ul>
              <li>Does not involve real money or real risk</li>
              <li>May not accurately reflect actual market conditions</li>
              <li>Order fills may be more favorable than in live trading</li>
              <li>Does not account for slippage, commissions, or market impact</li>
              <li>Emotional factors differ significantly from live trading</li>
              <li>Success in paper trading does NOT guarantee success in live trading</li>
            </ul>
          </section>

          <section>
            <h2>Pattern Day Trading Rule</h2>
            
            <p>
              FINRA rules define a "pattern day trader" as someone who executes 4 or more day trades 
              within 5 business days, provided the number of day trades is more than 6% of total trades 
              in the account during that period.
            </p>

            <p>
              Pattern day traders must maintain minimum equity of $25,000 in their account. If you fall 
              below this threshold, you may be restricted from day trading until you restore the minimum 
              equity.
            </p>
          </section>

          <section>
            <h2>Margin Trading</h2>
            
            <Card className="border-orange-500/50 bg-orange-500/10 my-4">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600 mt-1" />
                  <div className="text-sm">
                    <p className="font-semibold mb-2">Margin Trading Risks</p>
                    <p className="m-0">
                      Trading on margin involves borrowing money to buy securities. This increases both 
                      potential gains AND potential losses. You can lose more than your initial investment. 
                      Your broker may liquidate your positions without notice if your account falls below 
                      minimum maintenance requirements.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          <section>
            <h2>Market Data</h2>
            
            <p>
              Market data displayed on Tradeharbor.io may be delayed and may not reflect real-time 
              market conditions. We rely on third-party data providers and cannot guarantee the 
              accuracy, completeness, or timeliness of market data.
            </p>

            <p>
              Real-time market data may be available through certain subscription plans. Please verify 
              data accuracy before making trading decisions.
            </p>
          </section>

          <section>
            <h2>Regulatory Compliance</h2>
            
            <h3>Your Responsibilities</h3>
            <p>
              As a trader, you are responsible for:
            </p>
            <ul>
              <li>Understanding all risks associated with trading</li>
              <li>Complying with all applicable securities laws and regulations</li>
              <li>Accurately reporting your trading activity for tax purposes</li>
              <li>Maintaining adequate records of your transactions</li>
              <li>Reviewing confirmations and statements promptly</li>
              <li>Reporting any discrepancies or unauthorized activity immediately</li>
            </ul>

            <h3>Regulatory Bodies</h3>
            <p>
              If you have questions or concerns about your brokerage account, you may contact:
            </p>
            <ul>
              <li><strong>FINRA:</strong> (301) 590-6500 or www.finra.org</li>
              <li><strong>SEC:</strong> (202) 551-6551 or www.sec.gov</li>
              <li><strong>SIPC:</strong> (202) 371-8300 or www.sipc.org</li>
            </ul>
          </section>

          <section>
            <h2>Tax Considerations</h2>
            
            <p>
              Trading may have significant tax implications. You are responsible for understanding 
              and complying with all applicable tax laws. Consider consulting with a tax professional 
              regarding:
            </p>
            <ul>
              <li>Capital gains and losses reporting</li>
              <li>Wash sale rules</li>
              <li>Pattern day trader tax classification</li>
              <li>State and local tax obligations</li>
              <li>Retirement account trading restrictions</li>
            </ul>
          </section>

          <section>
            <h2>International Users</h2>
            
            <p>
              Tradeharbor.io and its brokerage partners primarily serve U.S. residents. International 
              users may be subject to additional restrictions, regulations, and tax obligations in their 
              home countries. We do not provide services to residents of certain countries. Please verify 
              your eligibility before opening an account.
            </p>
          </section>

          <section>
            <h2>Questions or Concerns</h2>
            
            <div className="not-prose">
              <Card>
                <CardContent className="py-4 space-y-3">
                  <div>
                    <p className="font-semibold">Contact Information</p>
                    <p className="text-sm text-muted-foreground">
                      Support: <a href="mailto:support@tradeharbor.io" className="text-primary hover:underline">support@tradeharbor.io</a>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Compliance: <a href="mailto:legal@tradeharbor.io" className="text-primary hover:underline">legal@tradeharbor.io</a>
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">Brokerage Services</p>
                    <p className="text-sm text-muted-foreground">
                      Alpaca Markets, Inc. - Member FINRA/SIPC<br />
                      <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        https://alpaca.markets
                      </a>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          <Card className="border-blue-500/50 bg-blue-500/10 my-8">
            <CardContent className="py-6">
              <p className="text-center font-semibold mb-2 m-0">
                Please read and understand these disclosures before using Tradeharbor.io
              </p>
              <p className="text-sm text-center m-0">
                By using our platform, you acknowledge that you have read, understood, and agree to 
                these disclosures and accept all associated risks.
              </p>
            </CardContent>
          </Card>

          <p className="text-sm text-muted-foreground text-center italic">
            These disclosures are effective as of February 11, 2026 and are subject to change.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <div className="flex gap-4">
              <Link href="/privacy">
                <a className="hover:text-primary transition-colors">Privacy Policy</a>
              </Link>
              <Link href="/terms">
                <a className="hover:text-primary transition-colors">Terms of Use</a>
              </Link>
              <Link href="/disclosures">
                <a className="hover:text-primary transition-colors">Disclosures</a>
              </Link>
            </div>
            <div>
              © 2026 Tradeharbor.io. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
